import React, { useEffect, useState } from "react";
import { Typography, Radio, Input, Textarea } from "@material-tailwind/react";

interface LawyerFeedbackFormProps {
  onChange: (data: any) => void;
}

type YesNoNa = "Sim" | "Não" | "Não se aplica" | null;

const toBooleanOrNull = (value: YesNoNa): boolean | null => {
  if (value === "Sim") return true;
  if (value === "Não") return false;
  return null; // "Não se aplica"
};

const LawyerFeedbackForm: React.FC<LawyerFeedbackFormProps> = ({ onChange }) => {
  const [formData, setFormData] = useState({
    receivedDefenseCopy: null as YesNoNa,
    receivedProcessGuidance: null as YesNoNa,
    receivedTestimonyGuidance: null as YesNoNa,
    lawyerName: "",
    arrivedOnTime: null as YesNoNa,
    instructionsClarity: "", // 1-10
    infoConfidence: "", // 1-10
    hearingConfidence: "", // 1-10
    optionalNotes: "",
  });

  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isValid, setIsValid] = useState(false);

  const requiredFields = [
    "receivedDefenseCopy",
    "receivedProcessGuidance",
    "receivedTestimonyGuidance",
    "lawyerName",
    "arrivedOnTime",
    "instructionsClarity",
    "infoConfidence",
    "hearingConfidence",
  ];

  const hasRatingBelowSeven = [
    formData.instructionsClarity,
    formData.infoConfidence,
    formData.hearingConfidence,
  ].some((rating) => rating !== "" && Number(rating) < 7);

  const validate = () => {
    const ok = requiredFields.every((f) => !!(formData as any)[f]);
    // Alguma nota abaixo de 7 exige o preenchimento do motivo
    const notesRequired = hasRatingBelowSeven && formData.optionalNotes.trim() === "";
    setIsValid(ok && !notesRequired);
  };

  useEffect(() => {
    validate();
  }, [formData]);

  useEffect(() => {
    onChange({
      ...formData,
      isValid,
      formattedData: {
        lawyerFeedback: {
          receivedDefenseCopy: toBooleanOrNull(formData.receivedDefenseCopy),
          receivedProcessGuidance: toBooleanOrNull(formData.receivedProcessGuidance),
          receivedTestimonyGuidance: toBooleanOrNull(formData.receivedTestimonyGuidance),
          lawyerName: formData.lawyerName.trim(),
          arrivedOnTime: toBooleanOrNull(formData.arrivedOnTime),
          instructionsClarity: Number(formData.instructionsClarity),
          infoConfidence: Number(formData.infoConfidence),
          hearingConfidence: Number(formData.hearingConfidence),
          optionalNotes: formData.optionalNotes?.trim() || "",
        },
      },
    });
  }, [formData, isValid, onChange]);

  const markTouched = (name: string) =>
    setTouched((t) => ({ ...t, [name]: true }));

  const isInvalid = (name: string) => touched[name] && !(formData as any)[name];

  const yesNoNaField = (name: keyof typeof formData, label: string) => (
    <div>
      <Typography variant="small" className="text-blue-700 font-medium mb-1">
        {label} <span className="text-red-500">*</span>
      </Typography>
      <div className="flex gap-4 flex-wrap">
        {(["Sim", "Não", "Não se aplica"] as const).map((option) => (
          <Radio
            key={option}
            name={name}
            label={option}
            checked={(formData as any)[name] === option}
            onChange={() => {
              markTouched(String(name));
              setFormData((s) => ({ ...s, [name]: option }));
            }}
          />
        ))}
      </div>
      {isInvalid(String(name)) && (
        <Typography className="text-red-500 text-xs mt-1">Este campo é obrigatório</Typography>
      )}
    </div>
  );

  const notesInvalid =
    hasRatingBelowSeven && touched.optionalNotes && formData.optionalNotes.trim() === "";

  return (
    <div className="space-y-4">
            {/* Nome do advogado */}
            <div>
        <Typography variant="small" className="text-blue-700 font-medium mb-1">
          Qual o nome do advogado que participou da audiência? <span className="text-red-500">*</span>
        </Typography>
        <Input
          type="text"
          name="lawyerName"
          value={formData.lawyerName}
          onChange={(e) => {
            markTouched("lawyerName");
            setFormData((s) => ({ ...s, lawyerName: e.target.value }));
          }}
          className={`w-full ${isInvalid("lawyerName") ? "border-red-500" : ""}`}
          maxLength={120}
        />
        {isInvalid("lawyerName") && (
          <Typography className="text-red-500 text-xs mt-1">Este campo é obrigatório</Typography>
        )}
      </div>

      {yesNoNaField("receivedDefenseCopy", "Você recebeu cópia da defesa?")}
      {yesNoNaField("receivedProcessGuidance", "Você recebeu orientações do advogado(a) sobre o processo?")}
      {yesNoNaField("receivedTestimonyGuidance", "Você recebeu orientações do advogado(a) de como manifestar-se no depoimento?")}
      {yesNoNaField("arrivedOnTime", "O advogado chegou/entrou no link com antecedência?")}

      {/* Escalas 1 a 10 */}
      {[
        {
          key: "instructionsClarity",
          label:
            "As instruções passadas pelo advogado foram claras? (1 = pouco claras, 10 = muito claras)",
        },
        {
          key: "infoConfidence",
          label:
            "O advogado foi seguro nas informações passadas? (1 = pouco seguro, 10 = muito seguro)",
        },
        {
          key: "hearingConfidence",
          label:
            "O advogado foi seguro na audiência? (1 = pouco seguro, 10 = muito seguro)",
        },
      ].map((item) => (
        <div key={item.key}>
          <Typography variant="small" className="text-blue-700 font-medium mb-1">
            {item.label} <span className="text-red-500">*</span>
          </Typography>
          <div className="w-full overflow-x-auto pb-2">
            <div className="flex gap-1 sm:gap-2 min-w-max w-full">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
              <Radio
                key={n}
                name={item.key}
                label={`${n}`}
                className="flex-shrink-0 [&>label]:gap-0.5 sm:[&>label]:gap-6 [&>label]:items-center"
                checked={(formData as any)[item.key] === String(n)}
                onChange={() => {
                  markTouched(item.key);
                  setFormData((s) => ({ ...s, [item.key]: String(n) }));
                }}
              />
            ))}
            </div>
          </div>
          {isInvalid(item.key) && (
            <Typography className="text-red-500 text-xs mt-1">Este campo é obrigatório</Typography>
          )}
        </div>
      ))}

      {/* Observação / motivo */}
      <div>
        <Typography variant="small" className="text-blue-700 font-medium mb-1">
          {hasRatingBelowSeven ? (
            <>
              Descreva o motivo da(s) nota(s) abaixo de 7 <span className="text-red-500">*</span>
            </>
          ) : (
            "Opcional: Caso exista alguma informação ou relato sobre a atuação do(a) advogado(a), por gentileza, descrever abaixo."
          )}
        </Typography>
        <Textarea
          name="optionalNotes"
          value={formData.optionalNotes}
          onChange={(e) => {
            markTouched("optionalNotes");
            setFormData((s) => ({ ...s, optionalNotes: e.target.value }));
          }}
          className={`w-full ${notesInvalid ? "border-red-500" : ""}`}
          placeholder={
            hasRatingBelowSeven
              ? "Explique os motivos das notas atribuídas"
              : "Descreva informações adicionais, se necessário"
          }
        />
        {notesInvalid && (
          <Typography className="text-red-500 text-xs mt-1">
            Este campo é obrigatório para notas abaixo de 7
          </Typography>
        )}
      </div>
    </div>
  );
};

export default LawyerFeedbackForm;
